import React from 'react';
import { useMediaQuery } from 'react-responsive';

import Text from 'common/components/Text/Text';
import { CommonHash, PersonalStatementQuestion } from 'app/types';
import TextQuestion from './components/TextQuestion';

import styles from './SelfIntroduction.module.scss';
import { LabelInputComponent } from './PersonalStatementBuilder';

interface CompProps {
  answers: CommonHash;
  setAnswer: (h: CommonHash) => void;
  questions: PersonalStatementQuestion[];
  personalStrengthsQuestion?: PersonalStatementQuestion;
  personalStrengths: CommonHash;
  setPersonalStrengths: React.Dispatch<React.SetStateAction<CommonHash>>;
  setInputOption: (
    key: string,
    value: any,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void
  ) => void;
  showPersonalStrengthsOptions: boolean;
  personalStrengthsOptions: string[];
  handleAddBulletOption: (
    option: string,
    setStateOptionsFunction: (value: React.SetStateAction<string[]>) => void,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void,
    stateToChange: CommonHash
  ) => void;
  setPersonalStrengthsOptions: React.Dispatch<React.SetStateAction<string[]>>;
  deleteBulletInputOption: (
    key: string,
    setStateFunction: (value: React.SetStateAction<CommonHash>) => void,
    stateToChange: CommonHash
  ) => void;
  dependencyTags: string[];
  setAvailableTags: React.Dispatch<React.SetStateAction<string[]>>;
}

const PersonalExperience = ({
  answers,
  setAnswer,
  questions,
  personalStrengthsQuestion,
  personalStrengths,
  setPersonalStrengths,
  setInputOption,
  showPersonalStrengthsOptions,
  personalStrengthsOptions,
  handleAddBulletOption,
  setPersonalStrengthsOptions,
  deleteBulletInputOption,
  dependencyTags,
  setAvailableTags,
}: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <div className={styles.stepContainer}>
      <Text size={24} weight={600} color="primary" inlineStyle={{ marginBottom: '40px' }}>
        Personal Experience
      </Text>
      {personalStrengthsQuestion && (
        <>
          <div className={styles.statementQuestion} style={{ marginTop: '100px' }}>
            {personalStrengthsQuestion?.title}&nbsp;
            {personalStrengthsQuestion?.min_content &&
              `(at least ${personalStrengthsQuestion.min_content} answers)`}
          </div>
          {personalStrengthsQuestion?.subtitle && (
            <div className={styles.questionSubtitle}>{personalStrengthsQuestion?.subtitle}</div>
          )}
          {Object.keys(personalStrengths.content).map((key) => (
            <LabelInputComponent
              key={key}
              keyNumber={key}
              setInputFunction={setInputOption}
              setObjectFunction={setPersonalStrengths}
              inputValue={personalStrengths.content[key]}
              placeholderText="Type in your answer"
              canBeDeleted={Object.keys(personalStrengths.content).length > 3}
              deleteBulletInputOption={deleteBulletInputOption}
              stateToChange={personalStrengths}
              dependencyTags={dependencyTags}
              setAvailableTags={setAvailableTags}
            />
          ))}
          <div
            className={styles.itemLabel}
            style={{ marginTop: '20px' }}
            onClick={() =>
              setInputOption(
                (Object.keys(personalStrengths['content']).length + 1).toString(),
                '',
                setPersonalStrengths
              )
            }
          >
            +
          </div>
          {!!showPersonalStrengthsOptions && (
            <div className={styles.optionsContainer}>
              {personalStrengthsOptions.map((answerOption, index) => (
                <div
                  className={styles.bulletOption}
                  key={index}
                  onClick={() =>
                    handleAddBulletOption(
                      answerOption,
                      setPersonalStrengthsOptions,
                      setPersonalStrengths,
                      personalStrengths
                    )
                  }
                >
                  <Text size={14} weight={600} inlineStyle={{ color: '#297DF2' }}>
                    {answerOption}
                  </Text>
                  <i className="bi bi-plus-circle" />
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {questions.map((question) => (
        <TextQuestion
          question={question}
          key={question.id}
          answers={answers}
          setAnswer={setAnswer}
          optOut={question.opt_out}
        />
      ))}
    </div>
  );
};

export default PersonalExperience;
